import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLogStream, LogEntry } from '@/hooks/useLogStream';
import { logController } from '@/utils/logController';
import logger from '@/utils/logger';

interface LoggingConfig {
  level: string;
  categories: string;
  streamingEnabled: boolean;
}

interface LoggingContextValue {
  config: LoggingConfig;
  isConnected: boolean;
  connectionError: string | null;
  updateConfig: (updates: Partial<LoggingConfig>) => void;
  reconnect: () => void;
}

const LoggingContext = createContext<LoggingContextValue | null>(null);

/**
 * Provider for managing logging configuration and streaming.
 * Handles SSE connection and log injection into console.
 */
export function LoggingProvider({ children }: { children: React.ReactNode }) {
  // Load initial config from sessionStorage
  const [config, setConfig] = useState<LoggingConfig>(() => {
    const stored = logController.loadConfig();
    return stored || {
      level: 'info',
      categories: '',
      streamingEnabled: false
    };
  });

  // Handle incoming logs from SSE stream
  const handleLog = useCallback((log: LogEntry) => {
    // Inject into console (unified console handles formatting based on streamingEnabled)
    logController.injectLog(log);
  }, []);

  // Connect to SSE stream if enabled
  const { isConnected, connectionError, reconnect } = useLogStream({
    enabled: config.streamingEnabled,
    level: config.level,
    categories: config.categories,
    onLog: handleLog
  });

  // Update configuration
  const updateConfig = useCallback((updates: Partial<LoggingConfig>) => {
    setConfig(prev => {
      const newConfig = { ...prev, ...updates };

      // Save to sessionStorage
      logController.saveConfig(newConfig);

      // Update controller filters
      logController.setFilters({
        level: newConfig.level,
        categories: newConfig.categories,
        showTiers: newConfig.streamingEnabled
      });

      logger.info('LoggingContext', 'Config updated', newConfig);
      return newConfig;
    });
  }, []);

  // Initialize controller filters on mount and when config changes
  useEffect(() => {
    logController.setFilters({
      level: config.level,
      categories: config.categories,
      showTiers: config.streamingEnabled
    });
    logger.info('LoggingContext', 'Filters initialized/updated', {
      level: config.level,
      categories: config.categories,
      streamingEnabled: config.streamingEnabled
    });
  }, [config.level, config.categories, config.streamingEnabled]);

  // Log connection status changes
  useEffect(() => {
    if (config.streamingEnabled) {
      if (isConnected) {
        logger.info('LoggingContext', 'Backend log streaming connected');
      } else if (connectionError) {
        logger.warn('LoggingContext', 'Backend log streaming error:', connectionError);
      }
    }
  }, [isConnected, connectionError, config.streamingEnabled]);

  const value: LoggingContextValue = {
    config,
    isConnected,
    connectionError,
    updateConfig,
    reconnect
  };

  return (
    <LoggingContext.Provider value={value}>
      {children}
    </LoggingContext.Provider>
  );
}

/**
 * Hook to access logging context.
 */
export function useLogging() {
  const context = useContext(LoggingContext);
  if (!context) {
    throw new Error('useLogging must be used within LoggingProvider');
  }
  return context;
}